import { useDispatch, useSelector } from "react-redux";
import React, { useEffect, useState, useMemo } from "react";
import Uwp, { UploadFileStatusEnum } from "./utils/uwp";
import Log from "./utils/log";
import { elementAction, feedbackAction } from "./store";
import { AppClient } from "./utils/tool/client";
import { Trans, withTranslation } from "react-i18next";
import {
  PrivacyNotificationDialog,
  SubmitDialog,
  ReportCodeDialog,
} from "./components/dialog";
import LikeForm from "./components/LikeForm";
import RadioButton from "./components/elements/RadioButton";
import TitleIcon from "./components/elements/TitleIcon";
import DislikeForm from "./components/DislikeForm";
import Keyboard from "./components/control/keyboard";

export default function Home({ t, i18n, isSetLanguage }) {
  const dispatch = useDispatch();
  const enableRemoteCode = useSelector((state) => state.uwp.enableRemoteCode);
  // const [selectedLikeType, setSelectedLikeType] = useState("");
  const selectedLikeType = useSelector(
    (state) => state.element.selectedLikeType
  );
  const privacyNotificationDialogId = useSelector(
    (state) => state.element.privacyNotificationDialogId
  );
  const reportCodeDialogId = useSelector(
    (state) => state.element.reportCodeDialogId
  );
  const reportCodeMode = useSelector((state) => state.element.reportCodeMode);
  const [showReportCodeInput, setShowReportCodeInput] = useState(true);
  const submitDialogId = useSelector((state) => state.element.submitDialogId);
  const errorDialogId = useSelector((state) => state.element.errorDialogId);
  const lang = useSelector((state) => state.element.lang);
  const isShowPrivacyPolicyDialog = useSelector(
    (state) => state.element.isShowPrivacyPolicyDialog
  );
  const isShowErrorDialog = useSelector(
    (state) => state.element.isShowErrorDialog
  );
  const isShowSubmitDialog = useSelector(
    (state) => state.element.isShowSubmitDialog
  );
  const isShowReportCodeDialog = useSelector(
    (state) => state.element.isShowReportCodeDialog
  );
  const isShowSelect = useSelector((state) => state.element.isShowSelect);
  const attachmentFileUploadDone = useSelector(
    (state) => state.feedback.attachmentFileUploadDone
  );
  const uploadStatus = useSelector((state) => state.uwp.uploadStatus);
  const uploadProgress = useSelector((state) => state.uwp.uploadProgress);
  const focusNameList = useSelector((state) => state.element.focusNameList);
  const theme = useSelector((state) => state.element.theme);
  // const [loadingImgType, setLoadingImgType] = useState("dark");
  const enableRootKeyboardEvent = useSelector(
    (state) => state.element.enableRootKeyboardEvent
  );
  const isPrivacyNotificationContentFocused = useSelector(
    (state) => state.element.isPrivacyNotificationContentFocused
  );
  useEffect(() => {
    if (
      !isShowReportCodeDialog &&
      !isShowSubmitDialog &&
      !isShowPrivacyPolicyDialog &&
      !isPrivacyNotificationContentFocused &&
      !isShowSelect
    ) {
      dispatch(elementAction.setEnableRootKeyboardEvent(true));
    } else {
      dispatch(elementAction.setEnableRootKeyboardEvent(false));
    }
  }, [
    isShowReportCodeDialog,
    isShowSubmitDialog,
    isShowPrivacyPolicyDialog,
    isShowSelect,
    isPrivacyNotificationContentFocused,
    dispatch,
  ]);
  useEffect(() => {
    Log.debug("[UWP] change upload status ", uploadStatus);
    if (uploadStatus === UploadFileStatusEnum.UploadFileFinish) {
      dispatch(feedbackAction.submitSuccess());
    }
    if (uploadStatus > UploadFileStatusEnum.PackageEnd) {
      if (uploadStatus % 100 === 0 || uploadStatus % 100 === 2) {
        dispatch(
          feedbackAction.setLogUploadProgress(80 + Number(uploadProgress) * 0.2)
        );
      }
      if (Number(uploadStatus) % 100 === 4) {
        Log.error("[UWP] upload fail, dispatch submitFailed", uploadStatus);
        dispatch(feedbackAction.submitFailed());
      }
    }
  }, [uploadStatus]);

  useEffect(() => {
    if (selectedLikeType === "") {
      Uwp.clearWebContent();
    } else {
      Uwp.changeWebContent();
    }
  }, [selectedLikeType]);

  const [focusOptions, setFocusOptions] = useState([
    ["radio_like", "radio_dislike"],
  ]);
  const likeOptions = useMemo(() => {
    const options = [];
    for (let i = 1; i <= 20; i++) {
      options.push(`#${i}`);
    }
    return options;
  }, []);

  useEffect(() => {
    setShowReportCodeInput(false);
  }, [focusOptions]);

  useEffect(() => {
    switch (selectedLikeType) {
      case "like":
        setFocusOptions([
          ["radio_like", "radio_dislike"],
          [...likeOptions],
          ["like_description"],
          ["like_submit"],
        ]);
        break;
      case "dislike":
        setFocusOptions([["radio_like", "radio_dislike"], ["dislike_type"]]);
        break;
      case "dislike_form":
        setFocusOptions([
          ["radio_like", "radio_dislike"],
          ["dislike_type"],
          ["first_category"],
          ["second_category"],
          ["description"],
          ["add_files"],
          ["email"],
          ["privacy_notification"],
          ["submit"],
        ]);
        break;
      case "dislike_contact_asus":
        setFocusOptions([
          ["radio_like", "radio_dislike"],
          ["dislike_type"],
          ["contact_asus"],
        ]);
        break;
      default:
        setFocusOptions([["radio_like", "radio_dislike", "showRemoteCode"]]);
        setShowReportCodeInput(true);
    }
  }, [selectedLikeType]);

  useEffect(() => {
    const isDislikeType =
      selectedLikeType === "dislike" ||
      selectedLikeType === "dislike_form" ||
      selectedLikeType === "dislike_contact_asus";

    // By incrementing the key, we force a remount and state reset of the component
    if (selectedLikeType === "like") {
      setDislikeFormKey((k) => k + 1);
    } else if (isDislikeType) {
      setLikeFormKey((k) => k + 1);
    }
  }, [selectedLikeType]);
  const [selected, setSelected] = useState([0, 0]);
  const [likeFormKey, setLikeFormKey] = useState(0);
  const [dislikeFormKey, setDislikeFormKey] = useState(0);

  function setSelectedLikeType(type) {
    dispatch(elementAction.setSelectedLikeType(type));
  }

  function isDislike() {
    return (
      selectedLikeType === "dislike" ||
      selectedLikeType === "dislike_form" ||
      selectedLikeType === "dislike_contact_asus"
    );
  }

  function triggerShowReportCode() {
    dispatch(elementAction.setReportCodeMode(0));
    dispatch(elementAction.triggerReportCodeDialog(true));
  }

  function showRemoteCode() {
    console.log(
      "showRemoteCode",
      enableRemoteCode,
      selectedLikeType === null,
      enableRemoteCode && selectedLikeType === null
    );
    return enableRemoteCode && selectedLikeType === null;
  }

  return (
    <div className="h-screen overflow-auto">
      <Keyboard
        isEnabled={enableRootKeyboardEvent}
        options={focusOptions}
        selected={selected}
        setSelected={setSelected}
        loop={false}
      />
      {/*<KeyboardComponent />*/}
      {!isSetLanguage && (
        <div
          aria-hidden="true"
          className={`font-sans fixed bg-${theme} top-0 bottom-0 left-0 right-0 flex flex-col justify-center items-center z-50 text-${theme}-normal`}
        >
          <img
            src={`${process.env.PUBLIC_URL}/assets/img/loading-${theme}.gif`}
            alt="logo"
            className="w-[100px] mt-[14px]"
          />
          <p className="mt-[18px] text-[14px]">{t("loading")}</p>
        </div>
      )}
      <div className={"p-5"}>
        <div className={"flex items-baseline space-x-3"}>
          <TitleIcon width={40} height={15} aria-hidden="true" />
          <p className={`title text-${theme}-normal`}>{t("init_title")}</p>
        </div>

        <div className={"flex flex-row space-x-10 my-4"}>
          <RadioButton
            id={"radio_like"}
            label={t("like_label")}
            className={"flex justify-start items-center"}
            theme={theme}
            isFocus={false}
            isEnabled={true}
            isChecked={selectedLikeType === "like"}
            onClick={() => {
              setSelectedLikeType("like");
            }}
          />
          <RadioButton
            id={"radio_dislike"}
            label={t("dislike_label")}
            className={"flex justify-start items-center"}
            theme={theme}
            isFocus={false}
            isEnabled={true}
            isChecked={isDislike()}
            onClick={() => {
              setSelectedLikeType("dislike");
            }}
          />
        </div>
        <div className={selectedLikeType === "like" ? "block" : "hidden"}>
          <LikeForm t={t} key={likeFormKey} />
        </div>
        <div className={isDislike() ? "block" : "hidden"}>
          <DislikeForm t={t} key={dislikeFormKey} />
        </div>
      </div>
      <PrivacyNotificationDialog t={t} id={privacyNotificationDialogId} />
      <ReportCodeDialog t={t} id={reportCodeDialogId} mode={reportCodeMode} />
      <SubmitDialog t={t} id={submitDialogId} />
      {showRemoteCode() && (
        <>
          <div className="p-5 absolute bottom-[5vh] ">
            <a
              id="showRemoteCode"
              data-id="showRemoteCode"
              href="#"
              tabIndex={0}
              role="link"
              aria-haspopup="dialog"
              onClick={(e) => {
                e.preventDefault();
                triggerShowReportCode();
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  triggerShowReportCode();
                }
              }}
              className={`cursor-pointer underline text-remote_code_link`}
            >
              {t("remote_code_description")}
            </a>
          </div>
        </>
      )}
    </div>
  );
}

// export default withTranslation()(App);
